import { PensionOption } from "../../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionArrangementType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { SourceType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { TransferType } from "../../../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { Instrument } from "../../../../../../../../Wrap/Models/Domains/Investment/Instrument";
import { InstrumentSearchQuery } from "../../../../../../../../Wrap/Models/Domains/Investment/InstrumentSearchQuery";
import { PensionProtectionDetails } from "../../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { PensionTransfer } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { ClientTradableInvestments } from "../../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/ClientTradableInvestments";
import { PensionBase } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { initAmount } from "../../../../../../../Shared/IST/Components/Shared/Utils";
import { InvestmentSelectionAction } from "../../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { BuyAction } from "../../../../Buy/Action/BuyAction";
import { PensionHelper } from "../../../Helpers/PensionHelper";
import { ChooseAssetsToSellAssets } from "../Store/DrawdownCalculationStoreStatic";
import { MoneyOrPercentageType } from "../../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { processTypeEnum } from "../../../Resource/Contents";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { ChooseAssetsToSellFor } from "../Resource/Contents";
import { InvestmentAllocation } from "../../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { Objects } from "../../../../../../../../react-core/Utils/Objects";
import { isNullOrEmpty } from "../../../../../../../../react-core/Utils/Arrays";
import { getAssetsToSellTotalForIncome } from "../../IncomePaymentDetails/Helper/IncomePaymentHelper";
import { ChargesHelper } from "../../../Helpers/ChargesHelper";

export const isCappedOrFlexIOrUFPLS = (data: DrawdownModel): boolean => {
    return (
        [DrawdownType.Capped, DrawdownType.Flexible].includes(data?.DrawdownType) ||
        data?.PensionOption == PensionOption.UFPLS
    );
};

export const isRegualr = (data: DrawdownModel): boolean => {
    return data?.DrawdownType == DrawdownType.Regular;
};

export const calculateBeneiftsCharge = (data: DrawdownModel, v: number, type: string) => {
    const ratio = type == "income" ? 0.25 : 0.55;
    return parseInt(
        (ratio * v * 0.01 * (data?.LifetimeAllowance?.ChargeableCrystallisationPortion?.Amount || 0)).toString()
    );
};

export const calculateBeneiftsNet = (data: DrawdownModel, v: number, type: string) => {
    return (
        (data?.LifetimeAllowance?.ChargeableCrystallisationPortion?.Amount || 0) * v * 0.01 -
        calculateBeneiftsCharge(data, v, type)
    );
};

export const showBenefitTable = (data: DrawdownModel, lessThan75: boolean, hasEnhancedPensionProtection: boolean) => {
    if (!lessThan75) {return false;}
    if (hasEnhancedPensionProtection) {return false;}
    if (isCappedOrFlexIOrUFPLS(data)) {
        if (data?.LifetimeAllowance?.ChargeableCrystallisationPortion?.Amount > 0) {
            return true;
        }
        if (getCrystallisationAmount(data) > 0.9 * data?.LifetimeAllowance?.AvailablePersonalAllowance?.Amount) {
            return true;
        }
        return false;
    }
    if (isRegualr(data)) {
        return data?.LifetimeAllowance?.EstimatedTotalSlaUsedAfterAYear > 100;
    }
    return false;
};

export const showBenefitLastTwoColumn = (data: DrawdownModel) => {
    if (data?.DrawdownType == DrawdownType.Regular) {return false;}
    return data?.LifetimeAllowance?.ChargeableCrystallisationPortion?.Amount > 0;
};

export const hasEnhancedPensionProtection = (data: PensionBase): boolean => {
    return (
        data?.HasPensionProtection &&
        data?.PensionProtection?.PensionEnhancedProtectionDetails?.PclsEntitlement !== undefined
    );
};

export const calculateChooseAssetsToSellTotal = (data: DrawdownModel, type: ChooseAssetsToSellFor): number => {
    if (type === ChooseAssetsToSellFor.Income) {
        return getAssetsToSellTotalForIncome(data);
    } else {
        return getAssetsToSellTotalForDrawdown(data);
    }
};

export const getAssetsToSellTotalForDrawdown = (data: DrawdownModel): number => {
    const charges =
        data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType == SourceType.ProductCash
            ? data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money?.Amount || 0
            : 0;
    return (
        charges +
        (data?.TaxFreeLumpSum?.Amount || 0) +
        (data?.ExcessBenefitsCalculations?.LumpSumTaxPayable?.Amount || 0) +
        (data?.ExcessBenefitsCalculations?.LumpSumTotalNetBenefits?.Amount || 0)
    );
};

export const showChooseAssetstoSell = (data: DrawdownModel, type: ChooseAssetsToSellFor): boolean => {
    if (data?.PensionOption == PensionOption.UFPLS) {return false;}
    if (data?.DrawdownType == DrawdownType.Regular) {
        if (type && type == ChooseAssetsToSellFor.Income) {
            return false;
        } else {
            return true;
        }
    }
    if ([DrawdownType.Capped, DrawdownType.Flexible].includes(data?.DrawdownType)) {
        if (type && type == ChooseAssetsToSellFor.Income) {
            return getAssetsToSellTotalForIncome(data) > 0;
        } else {
            return getAssetsToSellTotalForDrawdown(data) > 0;
        }
    }
    return false;
};

export const hasProtectedRetireAgeOrSchemeTaxFreeCash = (pensionProtection: PensionProtectionDetails) => {
    return (
        pensionProtection?.PensionSchemeSpecificProtectionDetails?.ProtectedRetirementAge &&
        pensionProtection?.PensionSchemeSpecificProtectionDetails?.PclsEntitlement
    );
};

export const hasPrimaryProtection = (pensionProtection: PensionProtectionDetails) => {
    return (
        pensionProtection.PensionPrimaryProtectionDetails?.PclsEntitlement &&
        pensionProtection.PensionPrimaryProtectionDetails?.PclsEntitlement > 0
    );
};

export const getCrystallisationAmount = (data: DrawdownModel) => {
    if (isCappedOrFlexIOrUFPLS(data)) {
        return data?.CrystallisationDetails?.CrystallisationAmount?.Amount || 0;
    }
    if (isRegualr(data)) {
        return data?.RegularCrystallisationDetails?.Amount?.Amount || 0;
    }
    return 0;
};

export const totalCashValue = (data: PensionBase): number => {
    if (!data) {return 0;}
    return (
        PensionHelper.singlePaymentsTotal(data) +
        PensionHelper.getTransfersTotal(
            (data?.PensionTransfers || []).filter(
                (ele) =>
                    ele.TransferType == TransferType.Cash && ele.PensionSource == PensionArrangementType.Accumulation
            )
        )
    );
};

export const totalReRegValue = (data: PensionBase) => {
    let value = 0;
    const preRetireReregTransfer = (data?.PensionTransfers || []).filter(
        (ele) => ele.TransferType == TransferType.InSpecie && ele.PensionSource == PensionArrangementType.Accumulation
    );
    value += PensionHelper.getTransfersTotal(preRetireReregTransfer);
    return value;
};

export const calculateUnCrystallizationAmount = (data: PensionBase, process: ProcessType, isITFC: boolean): number => {
    let preTotalCash = totalCashValue(data);
    const preRetireReregTransfer = (data?.PensionTransfers || []).filter(
        (ele) => ele.TransferType == TransferType.InSpecie && ele.PensionSource == PensionArrangementType.Accumulation
    );
    preTotalCash += isITFC ? 0 : totalReRegValue(data);
    const totalContribution = ChargesHelper.getTotalForIACEstimation(data, SubAccountType.Npr);
    let consolidateUncrystallisedValue = 0;
    if (process == ProcessType.TopUpWithITFLS || process == ProcessType.TakePensionBenefits) {
        // if process is topup / TPB, uncrystallizationAmount need add all existed accumulation arrangements amount value
        // consolidate uncrystallised value
        consolidateUncrystallisedValue = data?.DrawdownModel?.CrystallisationDetails?.UnCrystallisedValue?.Amount || 0;
    }
    const initalAdviceCharge =
        data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash
            ? 0
            : data?.InitialAdviserCharge?.Type == MoneyOrPercentageType.Money
            ? (preTotalCash * (data?.InitialAdviserCharge?.Money?.Amount || 0)) / totalContribution
            : ((data?.InitialAdviserCharge?.Percentage || 0) * preTotalCash) / 100;
    let residualValue = 0;
    if (!isITFC) {
        preRetireReregTransfer.forEach((e) => {
            residualValue += e?.ReRegisterValue?.Amount || 0;
        });
    }

    return (
        consolidateUncrystallisedValue +
        preTotalCash +
        residualValue -
        (process != ProcessType.TopUpWithITFLS ? initalAdviceCharge : 0)
    );
};

export const calculateNetDrawdownValue = (data: DrawdownModel, pensionProtection: PensionProtectionDetails) => {
    const isPrimary = pensionProtection?.PensionPrimaryProtectionDetails;
    const amount =
        (data?.CrystallisationDetails?.CrystallisationAmount?.Amount || 0) -
        (isPrimary ? data?.TaxFreeLumpSum?.Amount || 0 : data?.CurrentPclsEntitlement?.Amount || 0) -
        (data?.ExcessBenefitsCalculations?.LumpSumTaxPayable?.Amount || 0) -
        (data?.ExcessBenefitsCalculations?.AdditionalIncomeTaxPayable?.Amount || 0) -
        (data?.ExcessBenefitsCalculations?.LumpSumTotalNetBenefits?.Amount || 0);
    return amount;
};

export const calculateAvailableCash = (data: PensionBase, cashInvestmentId: number, process: ProcessType): number => {
    const uncrystallizationAmount = calculateUnCrystallizationAmount(data, process, false);
    const PercentageForCash =
        (data?.PreRetirementInstrumentSelections?.Instruments || []).find(
            (ele) => ele?.Instrument?.ProductId == cashInvestmentId
        )?.Single?.Percentage || 0;
    return uncrystallizationAmount * PercentageForCash * 0.01;
};

export const getChooseAssetsToSellTable = (data: PensionBase): ChooseAssetsToSellAssets[] => {
    const totalSingle: number = PensionHelper.singlePaymentsTotal(data);
    const totalClientRegular: number = PensionHelper.clientRegularPayment(data)?.Amount || 0;
    const totalThirdPartyRegular: number = PensionHelper.thirdPartyRegulaPayment(data)?.Amount || 0;
    const totalEmployerClientRegular: number = PensionHelper.employerRegularPayment(data)?.Amount || 0;
    const preTransfers: PensionTransfer[] = data.PensionTransfers
        ? data.PensionTransfers.filter(
              (ele) => ele.PensionSource == PensionArrangementType.Crystallised && ele.TransferType == TransferType.Cash
          )
        : [];
    const postTransfers: PensionTransfer[] = data.PensionTransfers
        ? data.PensionTransfers.filter(
              (ele) => ele.PensionSource == PensionArrangementType.Accumulation && ele.TransferType == TransferType.Cash
          )
        : [];
    const totalPre = preTransfers.reduce((total, ele) => {
        return total + initAmount(ele.CurrentValue);
    }, 0);
    const totalPost = postTransfers.reduce((total, ele) => {
        return total + initAmount(ele.CurrentValue);
    }, 0);
    const totalInstrument: ChooseAssetsToSellAssets[] = [];
    (data?.PreRetirementInstrumentSelections?.Instruments || []).forEach((element) => {
        if (!totalInstrument.find((item) => item.Id == element.Instrument.ProductId)) {
            const marketValue =
                (element.Single?.Percentage || 0) * 0.01 * totalSingle +
                (element.ClientRegular?.Percentage || 0) * 0.01 * totalClientRegular +
                (element.ThirdPartyRegular?.Percentage || 0) * 0.01 * totalThirdPartyRegular +
                (element.EmployerRegular?.Percentage || 0) * 0.01 * totalEmployerClientRegular +
                (element.Transfer?.Percentage || 0) * 0.01 * totalPre;
            totalInstrument.push({
                Assets: element.Instrument?.Name,
                MarketValue: { Amount: marketValue, Currency: CurrencyCode.GBP },
                Id: element.Instrument?.ProductId,
                ProductType: element?.Instrument?.Type,
                DisinvestAll: false,
                FactsheetUrl: element?.Instrument?.FinexDocumentUrl,
            });
        }
    });
    (data?.PostRetirementInstrumentSelections?.Instruments || []).forEach((element) => {
        if (!totalInstrument.find((item) => item.Id == element.Instrument.ProductId)) {
            const marketValue =
                (element.Single?.Percentage || 0) * 0.01 * totalSingle +
                (element.ClientRegular?.Percentage || 0) * 0.01 * totalClientRegular +
                (element.ThirdPartyRegular?.Percentage || 0) * 0.01 * totalThirdPartyRegular +
                (element.EmployerRegular?.Percentage || 0) * 0.01 * totalEmployerClientRegular +
                (element.Transfer?.Percentage || 0) * 0.01 * totalPost;
            totalInstrument.push({
                Assets: element.Instrument?.Name,
                MarketValue: { Amount: marketValue, Currency: CurrencyCode.GBP },
                Id: element.Instrument?.ProductId,
                ProductType: element?.Instrument?.Type,
                DisinvestAll: false,
                FactsheetUrl: element?.Instrument?.FinexDocumentUrl,
            });
        }
    });
    (data?.PensionTransfers || []).forEach((ele) => {
        (ele.Instruments || []).forEach((element) => {
            totalInstrument.push({
                Assets: element.Investment?.DisplayName,
                MarketValue: {
                    Amount: element?.Investment?.UnitPrice?.Amount * element.Units,
                    Currency: CurrencyCode.GBP,
                },
                Id: element.Investment?.ProductId,
                ProductType: element?.Investment?.Type,
                DisinvestAll: false,
                FactsheetUrl: "",
            });
        });
    });
    return totalInstrument;
};

export const getSellableAssets = (type: SubAccountType, hierarchyid, subAccountHierarchyId, callback?) => {
    BuyAction.getReuseHoldings(hierarchyid, subAccountHierarchyId, 0, (data: ClientTradableInvestments) => {
        const inidividual = data.CurrentIndividualHoldings.filter(
            (ele) =>
                (ele.CurrQuantity > 0 && !ele.HasOpenValueSellOrder && ele.MarketValue?.Amount > 0) ||
                ele.DisplayName == "GBP Cash"
        );
        const model = data.CurrentModelHoldings.filter(
            (ele) => ele.CurrQuantity > 0 && !ele.HasOpenValueSellOrder && ele.MarketValue?.Amount > 0
        );
        let ids = inidividual.map((ele) => ele.ProductId);
        ids = ids.concat(model.map((ele) => ele.ProductId));
        const query: InstrumentSearchQuery = {
            ProductWrapperTypes: [type],
            ProductIds: ids,
            CanBeSold: true,
            ResultsPerPage: ids.length,
            ServiceProposition: data.AccountServiceProposition,
        };
        const currentAssets: ChooseAssetsToSellAssets[] = [];
        InvestmentSelectionAction.searchInstruments(query, subAccountHierarchyId, (result: Instrument[]) => {
            result.forEach((ele) => {
                const obj = data.CurrentIndividualHoldings.find((item) => item.ProductId == ele.ProductId);
                const model = data.CurrentModelHoldings.find((item) => item.ProductId == ele.ProductId);
                if (obj) {
                    currentAssets.push({
                        Assets: obj.DisplayName,
                        MarketValue: obj.MarketValue,
                        ProductType: obj.Type,
                        Id: obj.ProductId,
                        DisinvestAll: false,
                        FactsheetUrl: obj.FinexFactSheetUrl,
                    });
                } else if (model) {
                    currentAssets.push({
                        Assets: model.DisplayName,
                        MarketValue: model.MarketValue,
                        ProductType: model.Type,
                        Id: model.ProductId,
                        DisinvestAll: false,
                        FactsheetUrl: model.FinexFactSheetUrl,
                    });
                }
            });
            if (callback) {callback(currentAssets);}
        });
    });
};

export const getUpdatedAssetsToSell = (
    usedAllocations: InvestmentAllocation[],
    usedForTotal: number,
    assets: ChooseAssetsToSellAssets[]
) => {
    if (isNullOrEmpty(usedAllocations) || usedForTotal == 0) {return assets;}
    const updatedAssets = Objects.cloneObject(assets);
    usedAllocations.map((ele, index) => {
        if (ele.Percentage > 0) {
            const assetIndex = updatedAssets.findIndex((x) => x.Id == ele.ProductId);
            if (assetIndex > -1) {
                updatedAssets[assetIndex].MarketValue = {
                    Amount: updatedAssets[assetIndex].MarketValue.Amount - usedForTotal * ele.Percentage * 0.01,
                    Currency: updatedAssets[assetIndex].MarketValue.Currency,
                };
            }
        }
    });
    return updatedAssets;
};

export const ShouldDisplayItfcChargesMessage = (
    processType: processTypeEnum,
    reason?: ChooseAssetsToSellFor
): boolean => {
    if (reason === ChooseAssetsToSellFor.Income) {return false;}
    const nonEligibleProcessTypes = [processTypeEnum.GadReview];
    return !nonEligibleProcessTypes.some((type) => type === processType);
};
